import { useEffect, useRef, useState } from 'react';
import { Printer, Shuffle, X } from 'lucide-react';
import WorkModalShell from '@/components/common/WorkModalShell';
import BrandLogo from '@/components/common/BrandLogo';
import { brandConfig } from '@/config/brandConfig';
import { brandSocialLinks } from '@/config/brandSocialLinks';
import { buildGreetingCards, type GreetingCard } from '@/lib/saleGreetings';
import { printHtmlContent } from '@/utils/printUtils';

export default function SalesGreetingCards({
  cards: initialCards,
  onClose,
  blankRecipient = false,
}: {
  cards: GreetingCard[];
  onClose: () => void;
  blankRecipient?: boolean;
}) {
  const [cards, setCards] = useState(initialCards);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const operation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current?.abort();
    };
  }, []);
  const print = async () => {
    const content = document.getElementById('selected-sales-greeting-cards');
    if (!content || operation.current) return;
    const controller = new AbortController();
    operation.current = controller;
    setPrinting(true);
    setError('');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => {
      operation.current = null;
      if (mounted.current) setPrinting(false);
    };
    try {
      await Promise.race([
        Promise.all(Array.from(content.querySelectorAll('img')).map((img) => img.decode())),
        new Promise((_, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new Error('Brand images could not be loaded. Check your connection and retry.'),
              ),
            5000,
          );
        }),
      ]);
      if (controller.signal.aborted || !mounted.current) return;
      printHtmlContent(content.outerHTML, {
        documentTitle: `${brandConfig.name} customer greetings`,
        signal: controller.signal,
        onAfterPrint: finish,
      });
    } catch (failure) {
      if (mounted.current && !controller.signal.aborted)
        setError('The logo or QR images could not be loaded. Check your connection and try again.');
      finish();
    } finally {
      clearTimeout(timer);
    }
  };
  return (
    <WorkModalShell open title="Customer greeting cards" onClose={onClose} busy={printing} wide>
      <header className="flex items-start justify-between gap-3 border-b border-border py-3">
        <div>
          <h2 className="text-lg font-semibold">Customer greeting cards</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {cards.length} {blankRecipient ? 'blank-name' : 'personalized'} cards · 100 original
            greetings · A4 with cut lines
          </p>
        </div>
        <button
          type="button"
          aria-label="Close greeting cards"
          disabled={printing}
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg hover:bg-muted disabled:opacity-50"
        >
          <X className="h-5 w-5" />
        </button>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto py-4">
        {error && (
          <p role="alert" className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        <div
          id="selected-sales-greeting-cards"
          style={{ fontFamily: 'Arial, sans-serif', color: '#16191d' }}
        >
          <style>{`
          .greeting-unit{margin-bottom:16px;break-inside:avoid;page-break-inside:avoid}
          .greeting-card{box-sizing:border-box;background:#fff;border:1px solid #d8cdb8;border-radius:10px;padding:22px;display:flex;flex-direction:column;gap:16px;overflow-wrap:anywhere}
          .greeting-card p,.greeting-card h3,.greeting-card figure{margin:0}.greeting-card header{display:flex;align-items:center;gap:10px;border-bottom:1px solid #c7a13f;padding-bottom:12px}
          .greeting-card .greeting-brand{flex:1;min-width:0}.greeting-card h3{font-size:18px;font-weight:bold;letter-spacing:.1em}.greeting-card .greeting-tagline{font-size:10px;color:#625746;margin-top:3px}
          .greeting-card .greeting-order{font-size:10px;color:#625746;text-align:right;max-width:35%}
          .greeting-card .greeting-dear{font-size:19px;font-weight:bold;line-height:1.3}.greeting-card .greeting-message{font-size:13px;line-height:1.5;margin-top:8px}
          .greeting-card blockquote{font-family:Georgia,serif;font-size:20px;line-height:1.35;text-align:center;color:#5c4824;margin:0;padding:8px 12px}
          .greeting-card .greeting-signature{font-size:11px;color:#625746}.greeting-card .greeting-join{text-align:center;font-size:10px;font-weight:bold;letter-spacing:.08em;text-transform:uppercase;margin-bottom:8px}
          .greeting-card .greeting-qrs{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;text-align:center}
          .greeting-card .greeting-qrs a{display:flex;flex-direction:column;align-items:center;text-decoration:none;color:inherit;min-width:0}
          .greeting-card .greeting-qrs img{display:block;width:28mm;height:28mm;max-width:100%;object-fit:contain;background:#fff}
          .greeting-card figcaption{font-size:10px;font-weight:bold;margin-top:3px}.greeting-card .greeting-caption{font-size:9px;color:#625746;font-weight:normal}
          .greeting-cut{display:flex;align-items:center;gap:8px;font-size:9px;color:#64748b;margin-top:10px}.greeting-cut::before,.greeting-cut::after{content:'';flex:1;border-top:1px dashed #64748b}
          @media screen and (max-width:639px){.greeting-card{padding:14px;gap:12px}.greeting-card h3{font-size:15px}.greeting-card .greeting-dear{font-size:17px}.greeting-card blockquote{font-size:18px;padding:4px}.greeting-card .greeting-qrs{gap:5px}.greeting-card figcaption{font-size:9px}}
          @media print{@page{size:A4 portrait;margin:10mm}.greeting-unit{margin:0 0 4mm}.greeting-unit:last-child{margin:0}.greeting-card{min-height:122mm;border-radius:0;padding:6mm;justify-content:space-between;gap:3mm}.greeting-card header{break-inside:avoid}.greeting-card .greeting-qrs{break-inside:avoid}.greeting-card .greeting-qrs img{width:28mm;height:28mm}.greeting-cut{break-inside:avoid}}
        `}</style>
          {cards.map((card, index) => (
            <section
              key={card.id}
              className="greeting-unit"
              aria-label={
                blankRecipient ? `Greeting template ${index + 1}` : `Greeting for ${card.number}`
              }
            >
              <article className="greeting-card">
                <header>
                  <BrandLogo width={40} height={40} className="h-10 w-10 shrink-0" />
                  <div className="greeting-brand">
                    <h3>{brandConfig.name.toUpperCase()}</h3>
                    <p className="greeting-tagline">{brandConfig.tagline}</p>
                  </div>
                  <p className="greeting-order">
                    A little thank-you
                    <br />
                    {card.number}
                  </p>
                </header>
                <div>
                  <p className="greeting-dear">
                    Dear {blankRecipient ? '____________________' : card.customerName},
                  </p>
                  <p className="greeting-message">{card.message}</p>
                  <p className="greeting-message">
                    Thank you for shopping with {brandConfig.name.toUpperCase()}.
                  </p>
                </div>
                <blockquote>“{card.quote}”</blockquote>
                <p className="greeting-signature">
                  With gratitude,
                  <br />
                  <strong>Team {brandConfig.name.toUpperCase()}</strong>
                </p>
                <div>
                  <p className="greeting-join">Stay close to the uncommon</p>
                  <div className="greeting-qrs">
                    {brandSocialLinks.map((link) => (
                      <figure key={link.id}>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          <img
                            src={link.qr}
                            alt={`${link.label} QR code`}
                            width={300}
                            height={300}
                          />
                        </a>
                        <figcaption>
                          {link.label}
                          <p className="greeting-caption">{link.caption}</p>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              </article>
              {index < cards.length - 1 && (
                <div className="greeting-cut" aria-hidden="true">
                  Cut here
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
      <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border pt-3">
        <button
          type="button"
          disabled={printing}
          onClick={() => setCards(buildGreetingCards(cards))}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm disabled:opacity-50"
        >
          <Shuffle className="h-4 w-4" />
          Shuffle greetings
        </button>
        <button
          type="button"
          disabled={printing}
          onClick={() => void print()}
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground disabled:opacity-50"
        >
          <Printer className="h-4 w-4" />
          {printing ? 'Preparing print…' : 'Print greeting cards'}
        </button>
      </footer>
    </WorkModalShell>
  );
}
